import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Home, Layers, Loader2, Sparkles, Eye } from "lucide-react";
import { Button } from "./ui/button";
import FractalGrid from "./FractalGrid";
import { getPublicMap } from "../lib/api";

export default function SharedMap() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [cache, setCache] = useState(null);
  const [path, setPath] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getPublicMap(shareId);
        const c = {};
        data.grids.forEach((g) => { c[g.path.join("::")] = g; });
        setCache(c);
        setPath(data.grids[0].path);
      } catch { setError(true); }
    })();
  }, [shareId]);

  const keyOf = (p) => p.join("::");
  const currentGrid = cache && cache[keyOf(path)];

  const onCell = (el) => {
    const newPath = [...path, el.label];
    if (cache[keyOf(newPath)]) setPath(newPath);
    else toast.info("This branch isn't part of the shared map. Sign up to explore deeper!");
  };

  if (error) {
    return (
      <div className="app-bg min-h-screen flex items-center justify-center px-6 text-center" data-testid="shared-map-error">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Shared map not found</h1>
          <p className="text-purple-200/60 mt-2">This link may be private or no longer exists.</p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-purple-600 hover:bg-purple-500 px-7 h-12">Go to FractalNotes</Button>
        </div>
      </div>
    );
  }

  if (!cache) {
    return <div className="app-bg min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-purple-300" /></div>;
  }

  return (
    <div className="app-bg min-h-screen" data-testid="shared-map-screen">
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-[#07070E]/80 border-b border-purple-500/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <div className="p-1.5 rounded-lg bg-purple-600/20 border border-purple-500/30"><Layers className="w-4 h-4 text-purple-300" /></div>
            <span className="font-display font-bold text-white hidden sm:block">FractalNotes</span>
          </div>
          <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0" data-testid="shared-breadcrumb">
            <Home className="w-3.5 h-3.5 text-purple-400 shrink-0" />
            {path.map((p, idx) => (
              <React.Fragment key={`${p}-${idx}`}>
                {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-purple-500/50 shrink-0" />}
                <button data-testid={`shared-breadcrumb-node-${idx}`} onClick={() => setPath(path.slice(0, idx + 1))}
                  className={"text-xs px-2 py-1 rounded-md whitespace-nowrap transition-colors " +
                    (idx === path.length - 1 ? "bg-purple-600/25 text-white font-semibold border border-purple-500/40"
                      : "text-purple-200/70 hover:text-white hover:bg-purple-500/10")}>
                  {p}
                </button>
              </React.Fragment>
            ))}
          </div>
          <span className="text-[10px] px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/20 text-purple-200/80 flex items-center gap-1 shrink-0">
            <Eye className="w-3 h-3" /> Read-only
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <AnimatePresence mode="wait">
          {currentGrid && (
            <motion.div key={keyOf(path)}
              initial={{ opacity: 0, scale: 0.96, filter: "blur(6px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.04, filter: "blur(6px)" }} transition={{ duration: 0.35 }}>
              <FractalGrid grid={currentGrid} atMaxDepth={false} onCellClick={onCell} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-10 text-center rounded-2xl bg-gradient-to-br from-purple-700/20 to-purple-950/40 border border-purple-500/25 p-8">
          <Sparkles className="w-6 h-6 text-purple-300 mx-auto" />
          <h2 className="font-display text-2xl font-bold text-white mt-3">Build your own infinite concept maps</h2>
          <p className="text-purple-200/70 mt-1">Turn any subject or your class notes into a recursive study grid with AI.</p>
          <Button data-testid="shared-signup-cta" onClick={() => navigate("/")}
            className="mt-5 rounded-full bg-purple-600 hover:bg-purple-500 text-white px-7 h-12 font-semibold shadow-[0_0_24px_rgba(168,85,247,0.4)]">
            Try FractalNotes free
          </Button>
        </div>
      </div>
    </div>
  );
}
